import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { productPropTypes } from '@nandosaus/prop-types';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';

import { analytics } from '../../../analytics';
import { Box, Flex } from '../../grid';
import { CartIndicator } from '../cart-indicator';
import { P } from '../../typography';
import { Tags } from '../../tags';
import { TextClamp } from '../../text-clamp';
import { useFeature } from '../../../hooks/use-feature';
import { LegacyCardWrapper } from './legacy-card-wrapper';
import { LegacyCard } from './legacy-card';

const MenuItemCard = ({ item, onClick, shouldKeepVertical, analyticsCategory }) => {
  const { CartStore, DeliveryStore } = useRootStore();
  const showDescription = useFeature('show-description');

  const { shortestDeliveryTime } = DeliveryStore;

  const itemPrice = item?.prices?.formattedPrice;
  const itemKilojoules = item?.formattedKilojoules;
  const isEligibleForDeliveryDiscount = item?.isEligibleForDeliveryDiscount;
  const hasOtherSizeEligibleForDeliveryDiscount = item?.hasOtherSizeEligibleForDeliveryDiscount;
  const isDiscountApplied = shortestDeliveryTime?.isDiscountApplied;

  const quantityInCart = parseInt(CartStore.productQuantityInCart(item.id), 10);
  const isItemInCart = quantityInCart > 0;

  const handleOnClick = () => {
    analytics.track(ANALYTICS_EVENTS.PRODUCT_CLICKED, {
      product_id: item.plu,
      name: item.name,
      category: item.categoryName,
      analyticsCategory,
    });

    onClick(item);
  };

  return (
    <LegacyCardWrapper
      onClick={item.isAvailable ? handleOnClick : undefined}
      quantityInCart={quantityInCart}
      minHeight={{ xs: '100px', md: '150px' }}
      data-item-id={item.id}
      isItemInCart={isItemInCart}
    >
      <LegacyCard
        flexWrap={{ xs: 'no-wrap' }}
        flexDirection={{ xs: shouldKeepVertical ? 'column' : 'row', md: 'column' }}
        contentWidth={{ xs: shouldKeepVertical ? 1 : 8 / 12, md: 1 }}
        contentHeight="100%"
        isDisabled={!item.isAvailable}
        image={
          item.image
            ? {
                url: item.image,
                alt: item.imageAlt,
                aspectRatio: '21:10',
                height: 250,
              }
            : null
        }
        imageHeight={{ xs: shouldKeepVertical ? '250px' : '150px', md: '250px' }}
        imageWidth={{ xs: shouldKeepVertical ? 1 : '150px', md: 1 }}
        justifyContent="space-between"
        padding={null}
        isItemInCart={isItemInCart}
        isEligibleForDeliveryDiscount={isEligibleForDeliveryDiscount || hasOtherSizeEligibleForDeliveryDiscount}
        isDiscountApplied={isDiscountApplied}
      >
        <Box position="relative" height="100%">
          {isItemInCart && (
            <CartIndicator
              position="absolute"
              right={{ xs: '12px', md: '6px' }}
              top={{ xs: null, md: '-20px' }}
              bottom={{ xs: '12px', md: null }}
              height={{ xs: 22, md: 40 }}
              width={{ xs: 22, md: 40 }}
              quantityInCart={quantityInCart}
            />
          )}
          <Box p={{ xs: shouldKeepVertical ? '0.75rem' : 1, xxl: '0.75rem' }}>
            <P
              variant={2}
              fontWeight="bold"
              mb={item.hasVariablePrice ? '0.5rem' : '1rem'}
              color={!item.isAvailable ? 'grey600' : undefined}
            >
              <TextClamp>{item.groupName || item.name}</TextClamp>
            </P>
            <Box mt={{ md: 'auto' }}>
              {item.hasVariablePrice && (
                <P variant={4} mb={{ xs: '.125rem', md: '.25rem' }} mt={0.5} color="greyPrimary">
                  From
                </P>
              )}
              <Flex flexDirection={{ xs: 'column', md: 'row' }} mb={0.5} justifyContent="space-between">
                <Flex>
                  {itemPrice && (
                    <P variant={3} mr=".5rem" color={!item.isAvailable ? 'greyPrimary' : undefined}>
                      {itemPrice}
                    </P>
                  )}
                  {itemKilojoules && (
                    <P color="greyPrimary" variant={3}>
                      {itemKilojoules}
                    </P>
                  )}
                </Flex>

                {item.formattedDietaryPreferencesList && (
                  <Box>
                    <Tags labels={item.formattedDietaryPreferencesList} size="small" />
                  </Box>
                )}
              </Flex>
            </Box>
            {showDescription && (
              <P mt="0.5rem" variant={3} color="greyPrimary">
                <TextClamp>{item.description}</TextClamp>
              </P>
            )}
          </Box>
        </Box>
      </LegacyCard>
    </LegacyCardWrapper>
  );
};

MenuItemCard.propTypes = {
  item: productPropTypes.isRequired,
  onClick: PropTypes.func.isRequired,
  shouldKeepVertical: PropTypes.bool,
  analyticsCategory: PropTypes.string,
};

MenuItemCard.defaultProps = {
  shouldKeepVertical: false,
  analyticsCategory: undefined,
};

/**
 * @deprecated to be replaced with MenuItemCard
 */
const LegacyMenuItemCard = observer(MenuItemCard);
export { LegacyMenuItemCard };
